import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { parseRole, type CurrentUser } from "@/domain/permissions";

export interface SupabaseConfig {
  url: string;
  anonKey: string;
}

let config: SupabaseConfig | null = null;
let client: SupabaseClient | null = null;

/** Sets the project from `/api/config`; must run before the first `getSupabase()`. */
export function configureSupabase(next: SupabaseConfig): void {
  config = next;
  client = null;
}

/** Supabase's default session key is `sb-<project ref>-auth-token`. */
const SESSION_KEY = /^sb-.+-auth-token/;

/** Removes sessions stored in `localStorage` by earlier versions of the app. */
function removeLegacyStoredSessions(): void {
  try {
    Object.keys(window.localStorage)
      .filter((key) => SESSION_KEY.test(key))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Storage may be unavailable (private mode); there is nothing to remove then.
  }
}

/**
 * Lazily created Supabase client. Only used in the "individual" auth mode,
 * so shared-mode deployments never contact Supabase. The session lives in
 * `sessionStorage`, so closing the tab or browser signs the user out.
 */
export function getSupabase(): SupabaseClient {
  if (!client) {
    if (!config) throw new Error("Supabase is not configured");
    removeLegacyStoredSessions();
    client = createClient(config.url, config.anonKey, {
      auth: { storage: window.sessionStorage },
    });
  }
  return client;
}

/**
 * Reads role and location from `app_metadata`, which only the service role
 * can write. A missing or unknown role yields `null` (fail closed).
 */
export function toCurrentUser(session: Session | null): CurrentUser | null {
  const user = session?.user;
  if (!user) return null;
  const meta = (user.app_metadata ?? {}) as { role?: unknown; location?: unknown };
  const role = parseRole(meta.role);
  if (!role) return null;
  return {
    id: user.id,
    email: user.email ?? "",
    role,
    location: typeof meta.location === "string" && meta.location ? meta.location : null,
  };
}

/** Throws the Supabase error (as a real `Error`) when a query failed. */
export function unwrap<T>(result: { data: T; error: { message: string } | null }): T {
  if (result.error) throw new Error(result.error.message);
  return result.data;
}
